// src/components/admin/sales/VendorsTab.tsx
// Gerenciamento de Vendedores + Ranking Integrado - REFATORADO GRAIA

import React, { useState } from 'react';
import {
    Users,
    TrendingUp,
    DollarSign,
    Eye,
    EyeOff,
    Percent,
    Download,
    X,
    Filter,
    Calendar,
    ChevronDown,
    ChevronUp,
    Upload,
    FileText,
    Clock,
    Award,
    Star,
    Zap,
    Trophy,
    Target,
    Trash2,
    AlertTriangle
} from 'lucide-react';
import './VendorsTab.css';
import type { VendorPayment } from '../../../types/marketplace.types';
import {
    useVendorsList,
    useVendorRanking,
    useVendorGoalRanking,
    useVendorAvailableMonths,
    useVendorPayments,
    useInvalidate,
} from '../../../hooks/useAdminQueries';

interface Vendor {
    _id: string;
    username: string;
    email: string;
    commissionRate: number;
    totalSoldCents: number;
    totalCommissionCents: number;
    paidCommissionCents: number;
    pendingCommissionCents: number;
    totalSales: number;
    activeCoupons: number;
    lastSale: string | null;
    enabled: boolean;
    joinedAt: string;
    paymentDay?: number;
}

interface MonthlyStats {
    month: string;
    salesCount: number;
    revenueCents: number;
}

interface VendorRanking {
    _id: string;
    username: string;
    email: string;
    position: number;
    timeInGraia: string;
    timeInGraiaMonths: number;
    isNewbie: boolean;
    salesCount: number;
    totalRevenueCents: number;
    platformRevenueCents: number;
    vendorCommissionCents: number;
    averageTicketCents: number;
    consistency: string;
    consistencyScore: number;
    monthlyStats: MonthlyStats[];
    commissionRate: number;
    active: boolean;
}

interface GoalRankingVendor {
    vendorId: string;
    vendorName: string;
    vendorEmail: string;
    commissionRate: number;
    position: number;
    goals: {
        type: 'revenue' | 'commission' | 'sales';
        targetValue: number;
        achievedValue: number;
        percentage: number;
        achieved: boolean;
    }[];
    summary: {
        totalGoals: number;
        achievedGoals: number;
        averagePercentage: number;
        overallScore: number;
    };
    salesData: {
        totalSales: number;
        totalRevenueCents: number;
        totalCommissionCents: number;
    };
}

interface AvailableMonth {
    month: number;
    year: number;
    vendorsCount: number;
    label: string;
}

interface VendorsTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const VendorsTab: React.FC<VendorsTabProps> = ({ onShowToast }) => {

    // ── 1. useState PRIMEIRO ──────────────────────────────
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
    const [rankingMode, setRankingMode] = useState<'sales' | 'goals'>('sales');
    const [rankingPeriod, setRankingPeriod] = useState('30');
    const [selectedMonth, setSelectedMonth] = useState<{ month: number; year: number } | null>(null);
    const [expandedVendorId, setExpandedVendorId] = useState<string | null>(null);

    // Modais
    const [showPaymentDayModal, setShowPaymentDayModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showCommissionModal, setShowCommissionModal] = useState(false);
    const [showToggleModal, setShowToggleModal] = useState(false);
    const [showDeletePaymentModal, setShowDeletePaymentModal] = useState(false);
    const [showGoalsModal, setShowGoalsModal] = useState(false);

    const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
    const [selectedVendorForPayment, setSelectedVendorForPayment] = useState<Vendor | null>(null);
    const [selectedVendorGoals, setSelectedVendorGoals] = useState<GoalRankingVendor | null>(null);
    const [paymentToDelete, setPaymentToDelete] = useState<{ id: string; vendorId: string; amount: number } | null>(null);
    const [paymentDay, setPaymentDay] = useState<number>(1);
    const [newCommissionRate, setNewCommissionRate] = useState(30);

    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [proofFile, setProofFile] = useState<File | null>(null);
    const [paymentNotes, setPaymentNotes] = useState('');
    const [uploadingPayment, setUploadingPayment] = useState(false);
    const [deletingPayment, setDeletingPayment] = useState(false);

    // ── 2. React Query hooks DEPOIS ───────────────────────
    const { data: vendorsData, isLoading: loading } = useVendorsList(filterStatus);
    const { data: rankingDataRes, isLoading: loadingRanking } = useVendorRanking(rankingMode, rankingPeriod);
    const { data: goalRankingDataRes } = useVendorGoalRanking(rankingMode, selectedMonth);
    const { data: monthsData } = useVendorAvailableMonths(rankingMode === 'goals');
    const { data: paymentsData } = useVendorPayments(expandedVendorId);
    const invalidate = useInvalidate();

    // ── 3. Derivados ──────────────────────────────────────
    const vendors = vendorsData?.vendors ?? [];
    const rankingData = rankingDataRes ?? null;
    const goalRankingData = goalRankingDataRes ?? null;
    const availableMonths: AvailableMonth[] = monthsData?.months ?? [];
    const currentPayments: VendorPayment[] = paymentsData?.payments ?? [];

    const toggleVendorExpand = (vendorId: string) => {
        setExpandedVendorId(prev => prev === vendorId ? null : vendorId);
    };

    const openPaymentDayModal = (vendor: Vendor) => {
        setSelectedVendorForPayment(vendor);
        setPaymentDay(vendor.paymentDay || 1);
        setShowPaymentDayModal(true);
    };

    const handleSavePaymentDay = async () => {
        if (!selectedVendorForPayment) return;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/vendors/${selectedVendorForPayment._id}/payment-day`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ paymentDay })
                }
            );

            if (response.ok) {
                onShowToast(`Dia de pagamento definido: ${paymentDay}`, 'success');
                setShowPaymentDayModal(false);
                setSelectedVendorForPayment(null);
                invalidate.vendorsList();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao definir dia', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao definir dia de pagamento', 'error');
        }
    };

    const openPaymentModal = (vendor: Vendor) => {
        setSelectedVendorForPayment(vendor);

        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

        setPeriodStart(lastMonth.toISOString().split('T')[0]);
        setPeriodEnd(lastMonthEnd.toISOString().split('T')[0]);
        setProofFile(null);
        setPaymentNotes('');
        setShowPaymentModal(true);
    };

    const handleRegisterPayment = async () => {
        if (!selectedVendorForPayment || !proofFile) {
            onShowToast('Selecione um comprovante', 'error');
            return;
        }

        try {
            setUploadingPayment(true);
            const token = localStorage.getItem('accessToken');

            const formData = new FormData();
            formData.append('proof', proofFile);
            formData.append('periodStart', periodStart);
            formData.append('periodEnd', periodEnd);
            if (paymentNotes) formData.append('notes', paymentNotes);

            const response = await fetch(
                `/api/admin/sales/vendors/${selectedVendorForPayment._id}/payments`,
                {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData
                }
            );

            if (response.ok) {
                onShowToast('Pagamento registrado e email enviado!', 'success');
                setShowPaymentModal(false);
                setSelectedVendorForPayment(null);
                invalidate.vendorsList();
                invalidate.vendorPayments(selectedVendorForPayment._id);
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao registrar pagamento', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao registrar pagamento', 'error');
        } finally {
            setUploadingPayment(false);
        }
    };

    const openDeletePaymentModal = (paymentId: string, vendorId: string, amount: number) => {
        setPaymentToDelete({ id: paymentId, vendorId, amount });
        setShowDeletePaymentModal(true);
    };

    const handleDeletePayment = async () => {
        if (!paymentToDelete) return;

        try {
            setDeletingPayment(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/payments/${paymentToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast('Histórico de pagamento excluído', 'success');
                setShowDeletePaymentModal(false);
                invalidate.vendorPayments(paymentToDelete.vendorId);
                invalidate.vendorsList();
                setPaymentToDelete(null);
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao excluir', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao excluir pagamento', 'error');
        } finally {
            setDeletingPayment(false);
        }
    };

    const openToggleModal = (vendor: Vendor) => {
        setSelectedVendor(vendor);
        setShowToggleModal(true);
    };

    const handleToggleVendor = async () => {
        if (!selectedVendor) return;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/vendors/${selectedVendor._id}/toggle`,
                {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast(
                    selectedVendor.enabled ? 'Vendedor desativado' : 'Vendedor ativado',
                    'success'
                );
                setShowToggleModal(false);
                setSelectedVendor(null);
                invalidate.vendorsList();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao alterar status', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao alterar status', 'error');
        }
    };

    const openCommissionModal = (vendor: Vendor) => {
        setSelectedVendor(vendor);
        setNewCommissionRate(vendor.commissionRate);
        setShowCommissionModal(true);
    };

    const handleAdjustCommission = async () => {
        if (!selectedVendor) return;

        if (newCommissionRate < 0 || newCommissionRate > 100) {
            onShowToast('Taxa deve estar entre 0% e 100%', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/vendors/${selectedVendor._id}/commission`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ commissionRate: newCommissionRate })
                }
            );

            if (response.ok) {
                onShowToast('Taxa de comissão atualizada!', 'success');
                setShowCommissionModal(false);
                setSelectedVendor(null);
                invalidate.vendorsList();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao ajustar taxa', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao ajustar taxa', 'error');
        }
    };

    const exportToCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                '/api/admin/sales/vendors/export',
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `vendedores-${Date.now()}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                onShowToast('CSV exportado com sucesso!', 'success');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao exportar CSV', 'error');
        }
    };

    const openGoalsModal = (vendor: GoalRankingVendor) => {
        setSelectedVendorGoals(vendor);
        setShowGoalsModal(true);
    };

    const formatCurrency = (cents: number) => `R$ ${(cents / 100).toFixed(2)}`;

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return 'Nunca';
        return new Date(dateStr).toLocaleDateString('pt-BR');
    };

    const getTimeAsVendor = (joinedAt: string) => {
        const diffDays = Math.floor((Date.now() - new Date(joinedAt).getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays < 30) return `${diffDays} dias`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} meses`;
        return `${Math.floor(diffDays / 365)} anos`;
    };

    const getNextPaymentDate = (paymentDay: number | undefined) => {
        if (!paymentDay) return 'Não definido';
        const now = new Date();
        const nextDate = now.getDate() >= paymentDay
            ? new Date(now.getFullYear(), now.getMonth() + 1, paymentDay)
            : new Date(now.getFullYear(), now.getMonth(), paymentDay);
        return nextDate.toLocaleDateString('pt-BR');
    };

    const getMedalIcon = (position: number) => {
        if (position === 1) return <Trophy size={20} className="vt-medal gold" />;
        if (position === 2) return <Trophy size={18} className="vt-medal silver" />;
        if (position === 3) return <Trophy size={16} className="vt-medal bronze" />;
        return <span className="vt-position-number">{position}º</span>;
    };

    const getConsistencyBadge = (consistency: string) => {
        const badges: Record<string, { class: string; icon: React.ReactNode }> = {
            'Alta': { class: 'high', icon: <Zap size={12} /> },
            'Média': { class: 'medium', icon: <TrendingUp size={12} /> },
            'Baixa': { class: 'low', icon: <DollarSign size={12} /> },
            'Sem vendas': { class: 'none', icon: <X size={12} /> }
        };
        const badge = badges[consistency] || badges['Baixa'];
        return (
            <span className={`vt-consistency-badge ${badge.class}`}>
                {badge.icon} {consistency}
            </span>
        );
    };

    const getGoalTypeName = (type: string) => {
        const names: Record<string, string> = { revenue: 'Receita', commission: 'Comissão', sales: 'Vendas' };
        return names[type] || type;
    };

    const getGoalTypeIcon = (type: string) => {
        if (type === 'revenue') return <DollarSign size={16} />;
        if (type === 'commission') return <TrendingUp size={16} />;
        if (type === 'sales') return <Users size={16} />;
        return <Target size={16} />;
    };

    const totalVendors = vendors.length;
    const activeVendors = vendors.filter((v: Vendor) => v.enabled).length;
    const totalRevenue = vendors.reduce((sum: number, v: Vendor) => sum + v.totalSoldCents, 0);
    const totalCommission = vendors.reduce((sum: number, v: Vendor) => sum + v.totalCommissionCents, 0);
    const totalSalesByVendors = rankingData?.summary?.totalSales || 0;

    if (loading) {
        return (
            <div className="vt-loading">
                <div className="vt-spinner"></div>
                <p>Carregando vendedores...</p>
            </div>
        );
    }

    return (
        <div className="vt-container">
            {/* HEADER */}
            <div className="vt-header">
                <div>
                    <h2>Gerenciamento de Vendedores</h2>
                    <p className="vt-subtitle">Acompanhe e gerencie sua equipe de vendas</p>
                </div>
                <button className="vt-btn-export" onClick={exportToCSV}>
                    <Download size={18} />
                    Exportar CSV
                </button>
            </div>

            {/* CARDS DE RESUMO */}
            <div className="vt-summary-container">
                <div className="vt-summary-grid">
                    <div className="vt-summary-card">
                        <div className="vt-summary-icon" style={{ background: '#3b82f6' }}>
                            <Users size={24} />
                        </div>
                        <div className="vt-summary-info">
                            <span className="vt-summary-label">Total de Vendedores</span>
                            <span className="vt-summary-value">{totalVendors}</span>
                            <span className="vt-summary-detail">{activeVendors} ativos</span>
                        </div>
                    </div>

                    <div className="vt-summary-card">
                        <div className="vt-summary-icon" style={{ background: '#003BD1' }}>
                            <TrendingUp size={24} />
                        </div>
                        <div className="vt-summary-info">
                            <span className="vt-summary-label">Receita Gerada</span>
                            <span className="vt-summary-value">{formatCurrency(totalRevenue)}</span>
                            <span className="vt-summary-detail">Por vendedores</span>
                        </div>
                    </div>

                    <div className="vt-summary-card">
                        <div className="vt-summary-icon" style={{ background: '#10b981' }}>
                            <DollarSign size={24} />
                        </div>
                        <div className="vt-summary-info">
                            <span className="vt-summary-label">Comissões Totais</span>
                            <span className="vt-summary-value">{formatCurrency(totalCommission)}</span>
                            <span className="vt-summary-detail">Pagas + Pendentes</span>
                        </div>
                    </div>

                    <div className="vt-summary-card">
                        <div className="vt-summary-icon" style={{ background: '#8b5cf6' }}>
                            <Award size={24} />
                        </div>
                        <div className="vt-summary-info">
                            <span className="vt-summary-label">Total de Vendas</span>
                            <span className="vt-summary-value">{totalSalesByVendors}</span>
                            <span className="vt-summary-detail">Feitas por vendedores</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABELA DE GERENCIAMENTO */}
            <div className="vt-management-container">
                <div className="vt-filters">
                    <div className="vt-filter-group">
                        <Filter size={18} />
                        <span>Filtrar:</span>
                        <button className={filterStatus === 'all' ? 'active' : ''} onClick={() => setFilterStatus('all')}>Todos</button>
                        <button className={filterStatus === 'active' ? 'active' : ''} onClick={() => setFilterStatus('active')}>Ativos</button>
                        <button className={filterStatus === 'inactive' ? 'active' : ''} onClick={() => setFilterStatus('inactive')}>Inativos</button>
                    </div>
                </div>

                {vendors.length === 0 ? (
                    <div className="vt-empty">
                        <Users size={64} />
                        <h3>Nenhum vendedor encontrado</h3>
                        <p>Crie contas de vendedores na aba "Contas"</p>
                    </div>
                ) : (
                    <div className="vt-table-wrapper">
                        <table className="vt-table">
                            <thead>
                                <tr>
                                    <th>Vendedor</th>
                                    <th>Taxa</th>
                                    <th>Total Vendido</th>
                                    <th>Comis. Paga</th>
                                    <th>Comis. Pend.</th>
                                    <th>Próx. Pagto</th>
                                    <th>Status</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vendors.map((vendor: Vendor) => (
                                    <React.Fragment key={vendor._id}>
                                        <tr className={!vendor.enabled ? 'vt-inactive-row' : ''}>
                                            <td>
                                                <div className="vt-vendor-info">
                                                    <strong>{vendor.username}</strong>
                                                    <span className="vt-vendor-email">{vendor.email}</span>
                                                    <span className="vt-vendor-time">
                                                        <Calendar size={12} />
                                                        {getTimeAsVendor(vendor.joinedAt)} no time
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <button className="vt-btn-commission" onClick={() => openCommissionModal(vendor)} title="Ajustar taxa">
                                                    <Percent size={14} />
                                                    {vendor.commissionRate}%
                                                </button>
                                            </td>
                                            <td className="vt-value">{formatCurrency(vendor.totalSoldCents)}</td>
                                            <td className="vt-value vt-success">{formatCurrency(vendor.paidCommissionCents)}</td>
                                            <td className="vt-value vt-pending">{formatCurrency(vendor.pendingCommissionCents)}</td>
                                            <td>
                                                <div className="vt-payment-date-cell">
                                                    <span>{getNextPaymentDate(vendor.paymentDay)}</span>
                                                    <button className="vt-btn-set-date" onClick={() => openPaymentDayModal(vendor)} title="Definir data">
                                                        <Calendar size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`vt-status-badge ${vendor.enabled ? 'active' : 'inactive'}`}>
                                                    {vendor.enabled ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="vt-vendor-actions">
                                                    <button
                                                        className={`vt-btn-toggle ${vendor.enabled ? 'active' : ''}`}
                                                        onClick={() => openToggleModal(vendor)}
                                                        title={vendor.enabled ? 'Desativar' : 'Ativar'}
                                                    >
                                                        {vendor.enabled ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                    <button className="vt-btn-expand" onClick={() => toggleVendorExpand(vendor._id)} title="Ver pagamentos">
                                                        {expandedVendorId === vendor._id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>

                                        {expandedVendorId === vendor._id && (
                                            <tr className="vt-expanded-row">
                                                <td colSpan={8}>
                                                    <div className="vt-payments-history">
                                                        <div className="vt-payments-header">
                                                            <h4>Histórico de Pagamentos</h4>
                                                            <button className="vt-btn-new-payment" onClick={() => openPaymentModal(vendor)}>
                                                                <Upload size={16} />
                                                                Registrar Pagamento
                                                            </button>
                                                        </div>

                                                        {currentPayments.length === 0 ? (
                                                            <div className="vt-payments-empty">
                                                                <FileText size={32} />
                                                                <p>Nenhum pagamento registrado ainda</p>
                                                            </div>
                                                        ) : (
                                                            <table className="vt-payments-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Data Pagto</th>
                                                                        <th>Período</th>
                                                                        <th>Valor</th>
                                                                        <th>Status</th>
                                                                        <th>Comprovante</th>
                                                                        <th>Ações</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {currentPayments.map((payment: VendorPayment) => (
                                                                        <tr key={payment._id}>
                                                                            <td>{formatDate(payment.paymentDate)}</td>
                                                                            <td>{formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}</td>
                                                                            <td className="vt-value vt-success">{formatCurrency(payment.totalCommissionCents)}</td>
                                                                            <td>
                                                                                <span className={`vt-status-badge ${payment.status}`}>
                                                                                    {payment.status === 'paid' ? 'Pago' : 'Pendente'}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                {payment.proofUrl ? (
                                                                                    <a href={`/api/admin/sales/payments/${payment._id}/proof`} className="vt-btn-download-proof" download>
                                                                                        <Download size={14} />
                                                                                        Baixar
                                                                                    </a>
                                                                                ) : (
                                                                                    <span className="vt-no-proof">Sem comprovante</span>
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                <button
                                                                                    className="vt-btn-delete-payment"
                                                                                    onClick={() => openDeletePaymentModal(payment._id, vendor._id, payment.totalCommissionCents)}
                                                                                    title="Excluir pagamento"
                                                                                >
                                                                                    <Trash2 size={14} />
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* DESTAQUES DO PERÍODO */}
            <div className="vt-highlights-container">
                <h3><Star size={20} /> Destaques do Período</h3>

                {loadingRanking ? (
                    <div className="vt-highlights-loading">
                        <div className="vt-spinner-small"></div>
                        <span>Carregando destaques...</span>
                    </div>
                ) : (
                    <div className="vt-highlights-grid">
                        {rankingData?.highlights?.topVendor && (
                            <div className="vt-highlight-card gold">
                                <div className="vt-highlight-icon"><Trophy size={32} /></div>
                                <div className="vt-highlight-content">
                                    <h4>Melhor Vendedor</h4>
                                    <p className="vt-highlight-name">{rankingData.highlights.topVendor.username}</p>
                                    <p className="vt-highlight-stat">{formatCurrency(rankingData.highlights.topVendor.platformRevenueCents)}</p>
                                </div>
                            </div>
                        )}
                        {rankingData?.highlights?.topRevenueGenerator && (
                            <div className="vt-highlight-card green">
                                <div className="vt-highlight-icon"><DollarSign size={32} /></div>
                                <div className="vt-highlight-content">
                                    <h4>Maior Receita</h4>
                                    <p className="vt-highlight-name">{rankingData.highlights.topRevenueGenerator.username}</p>
                                    <p className="vt-highlight-stat">{formatCurrency(rankingData.highlights.topRevenueGenerator.platformRevenueCents)}</p>
                                </div>
                            </div>
                        )}
                        {rankingData?.highlights?.mostConsistent && (
                            <div className="vt-highlight-card blue">
                                <div className="vt-highlight-icon"><Zap size={32} /></div>
                                <div className="vt-highlight-content">
                                    <h4>Mais Constante</h4>
                                    <p className="vt-highlight-name">{rankingData.highlights.mostConsistent.username}</p>
                                    <p className="vt-highlight-stat">{rankingData.highlights.mostConsistent.consistency}</p>
                                </div>
                            </div>
                        )}
                        {rankingData?.highlights?.bestNewbie && (
                            <div className="vt-highlight-card purple">
                                <div className="vt-highlight-icon"><Award size={32} /></div>
                                <div className="vt-highlight-content">
                                    <h4>Melhor Novato</h4>
                                    <p className="vt-highlight-name">{rankingData.highlights.bestNewbie.username}</p>
                                    <p className="vt-highlight-stat">{formatCurrency(rankingData.highlights.bestNewbie.platformRevenueCents)}</p>
                                </div>
                            </div>
                        )}
                        {goalRankingData?.highlights?.topPerformer && (
                            <div className="vt-highlight-card orange">
                                <div className="vt-highlight-icon"><Target size={32} /></div>
                                <div className="vt-highlight-content">
                                    <h4>Melhor Performance</h4>
                                    <p className="vt-highlight-name">{goalRankingData.highlights.topPerformer.vendorName}</p>
                                    <p className="vt-highlight-stat">Score: {goalRankingData.highlights.topPerformer.summary.overallScore}%</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* RANKING COMPLETO */}
            <div className="vt-ranking-container">
                <div className="vt-ranking-header">
                    <h3><Trophy size={20} /> Ranking Completo</h3>
                    <div className="vt-ranking-controls">
                        <div className="vt-ranking-mode">
                            <button className={rankingMode === 'sales' ? 'active' : ''} onClick={() => setRankingMode('sales')}>
                                <TrendingUp size={16} /> Por Vendas
                            </button>
                            <button className={rankingMode === 'goals' ? 'active' : ''} onClick={() => setRankingMode('goals')}>
                                <Target size={16} /> Por Metas
                            </button>
                        </div>

                        {rankingMode === 'sales' && (
                            <div className="vt-period-selector">
                                <span>Período:</span>
                                {['7', '30', '90', '365', 'all'].map((p) => (
                                    <button key={p} className={rankingPeriod === p ? 'active' : ''} onClick={() => setRankingPeriod(p)}>
                                        {p === 'all' ? 'Tudo' : `${p}d`}
                                    </button>
                                ))}
                            </div>
                        )}

                        {rankingMode === 'goals' && availableMonths.length > 0 && (
                            <div className="vt-month-selector">
                                <span>Mês:</span>
                                <select
                                    value={selectedMonth ? `${selectedMonth.year}-${selectedMonth.month}` : ''}
                                    onChange={(e) => {
                                        const [year, month] = e.target.value.split('-').map(Number);
                                        setSelectedMonth({ month, year });
                                    }}
                                    className="vt-month-select"
                                >
                                    {availableMonths.map((m: AvailableMonth) => (
                                        <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                                            {m.label} ({m.vendorsCount} vendedores)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {loadingRanking ? (
                    <div className="vt-ranking-loading">
                        <div className="vt-spinner"></div>
                        <p>Carregando ranking...</p>
                    </div>
                ) : rankingMode === 'sales' && rankingData && rankingData.ranking.length > 0 ? (
                    <div className="vt-ranking-table-wrapper">
                        <table className="vt-ranking-table">
                            <thead>
                                <tr>
                                    <th className="vt-col-pos">Pos.</th>
                                    <th className="vt-col-vendor">Vendedor</th>
                                    <th className="vt-col-time">Tempo</th>
                                    <th className="vt-col-revenue">Receita</th>
                                    <th className="vt-col-commission">Comissão</th>
                                    <th className="vt-col-sales">Vendas</th>
                                    <th className="vt-col-ticket">Ticket Médio</th>
                                    <th className="vt-col-consistency">Constância</th>
                                    <th className="vt-col-status">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rankingData.ranking.map((vendor: VendorRanking) => (
                                    <tr key={vendor._id} className={vendor.isNewbie ? 'vt-newbie-row' : ''}>
                                        <td className="vt-position-cell">{getMedalIcon(vendor.position)}</td>
                                        <td className="vt-vendor-cell">
                                            <div className="vt-vendor-info-ranking">
                                                <span className="vt-vendor-name">
                                                    {vendor.username}
                                                    {vendor.isNewbie && <span className="vt-newbie-badge">Novo</span>}
                                                </span>
                                                <span className="vt-vendor-email">{vendor.email}</span>
                                            </div>
                                        </td>
                                        <td><span className="vt-time-badge">{vendor.timeInGraia}</span></td>
                                        <td className="vt-revenue-cell">{formatCurrency(vendor.platformRevenueCents)}</td>
                                        <td className="vt-commission-cell">
                                            <div className="vt-commission-info">
                                                <span>{formatCurrency(vendor.vendorCommissionCents)}</span>
                                                <span className="vt-commission-rate">({vendor.commissionRate}%)</span>
                                            </div>
                                        </td>
                                        <td className="vt-sales-cell">{vendor.salesCount}</td>
                                        <td>{formatCurrency(vendor.averageTicketCents)}</td>
                                        <td>{getConsistencyBadge(vendor.consistency)}</td>
                                        <td>
                                            <span className={`vt-status-badge ${vendor.active ? 'active' : 'inactive'}`}>
                                                {vendor.active ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : rankingMode === 'goals' && goalRankingData && goalRankingData.ranking.length > 0 ? (
                    <div className="vt-ranking-table-wrapper">
                        <table className="vt-ranking-table">
                            <thead>
                                <tr>
                                    <th className="vt-col-pos">Pos.</th>
                                    <th className="vt-col-vendor">Vendedor</th>
                                    <th className="vt-col-goals">Metas</th>
                                    <th className="vt-col-achieved">Atingidas</th>
                                    <th className="vt-col-score">Score</th>
                                    <th className="vt-col-revenue">Receita</th>
                                    <th className="vt-col-commission">Comissão</th>
                                    <th className="vt-col-sales">Vendas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {goalRankingData.ranking.map((vendor: GoalRankingVendor) => (
                                    <tr key={vendor.vendorId}>
                                        <td className="vt-position-cell">{getMedalIcon(vendor.position)}</td>
                                        <td className="vt-vendor-cell">
                                            <div className="vt-vendor-info-ranking">
                                                <span className="vt-vendor-name">{vendor.vendorName}</span>
                                                <span className="vt-vendor-email">{vendor.vendorEmail}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="vt-goals-summary-cell">
                                                <div className="vt-goals-pills">
                                                    {vendor.goals.slice(0, 2).map((goal, idx: number) => (
                                                        <span key={idx} className={`vt-goal-pill ${goal.achieved ? 'achieved' : 'not-achieved'}`} title={`${getGoalTypeName(goal.type)}: ${goal.percentage}%`}>
                                                            {getGoalTypeIcon(goal.type)} {goal.percentage}%
                                                        </span>
                                                    ))}
                                                </div>
                                                <button className="vt-btn-view-goals" onClick={() => openGoalsModal(vendor)} title="Ver todas as metas">
                                                    Ver detalhes
                                                </button>
                                            </div>
                                        </td>
                                        <td className="vt-achieved-cell">
                                            <span className="vt-achieved-badge">{vendor.summary.achievedGoals}/{vendor.summary.totalGoals}</span>
                                        </td>
                                        <td className="vt-score-cell">
                                            <div className="vt-score-container">
                                                <span className="vt-score-value">{vendor.summary.overallScore}%</span>
                                                <div className="vt-score-bar">
                                                    <div className="vt-score-fill" style={{ width: `${Math.min(vendor.summary.overallScore, 100)}%` }}></div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="vt-revenue-cell">{formatCurrency(vendor.salesData.totalRevenueCents)}</td>
                                        <td className="vt-commission-cell">
                                            <div className="vt-commission-info">
                                                <span>{formatCurrency(vendor.salesData.totalCommissionCents)}</span>
                                                <span className="vt-commission-rate">({vendor.commissionRate}%)</span>
                                            </div>
                                        </td>
                                        <td className="vt-sales-cell">{vendor.salesData.totalSales}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="vt-ranking-empty">
                        <Trophy size={64} />
                        <h4>Nenhum vendedor no ranking</h4>
                        <p>Não há dados para exibir neste período</p>
                    </div>
                )}
            </div>

            {/* MODAL: DEFINIR DIA DE PAGAMENTO */}
            {showPaymentDayModal && selectedVendorForPayment && (
                <div className="vt-modal-overlay" onClick={() => setShowPaymentDayModal(false)}>
                    <div className="vt-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="vt-modal-header">
                            <h3>Definir Dia de Pagamento</h3>
                            <button className="vt-btn-close-modal" onClick={() => setShowPaymentDayModal(false)}><X size={20} /></button>
                        </div>
                        <div className="vt-modal-body">
                            <p className="vt-modal-vendor-name"><strong>{selectedVendorForPayment.username}</strong></p>
                            <p className="vt-modal-description">Escolha o dia do mês (1-28) em que as comissões serão pagas. A data se repetirá automaticamente.</p>
                            <div className="vt-payment-day-input-group">
                                <label>Dia do Mês</label>
                                <input type="number" min="1" max="28" value={paymentDay} onChange={(e) => setPaymentDay(Math.min(28, Math.max(1, Number(e.target.value))))} className="vt-payment-day-input" />
                                <input type="range" min="1" max="28" value={paymentDay} onChange={(e) => setPaymentDay(Number(e.target.value))} className="vt-payment-day-slider" />
                                <div className="vt-slider-labels"><span>Dia 1</span><span>Dia 14</span><span>Dia 28</span></div>
                                <p className="vt-next-payment-preview">Próximo pagamento: <strong>{getNextPaymentDate(paymentDay)}</strong></p>
                            </div>
                        </div>
                        <div className="vt-modal-footer">
                            <button className="vt-btn-cancel" onClick={() => setShowPaymentDayModal(false)}>Cancelar</button>
                            <button className="vt-btn-confirm" onClick={handleSavePaymentDay}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: REGISTRAR PAGAMENTO */}
            {showPaymentModal && selectedVendorForPayment && (
                <div className="vt-modal-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="vt-modal-content vt-modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="vt-modal-header">
                            <h3>Registrar Pagamento</h3>
                            <button className="vt-btn-close-modal" onClick={() => setShowPaymentModal(false)}><X size={20} /></button>
                        </div>
                        <div className="vt-modal-body">
                            <p className="vt-modal-vendor-name"><strong>{selectedVendorForPayment.username}</strong></p>
                            <div className="vt-form-group">
                                <label>Período Referente</label>
                                <div className="vt-date-range">
                                    <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="vt-date-input" />
                                    <span>até</span>
                                    <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="vt-date-input" />
                                </div>
                            </div>
                            <div className="vt-form-group">
                                <label>Comprovante de Pagamento *</label>
                                <div className="vt-file-upload">
                                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setProofFile(e.target.files?.[0] || null)} id="proof-upload" className="vt-file-input" />
                                    <label htmlFor="proof-upload" className="vt-file-label">
                                        <Upload size={18} />
                                        {proofFile ? proofFile.name : 'Escolher arquivo (PDF, PNG, JPG)'}
                                    </label>
                                </div>
                            </div>
                            <div className="vt-form-group">
                                <label>Observações (opcional)</label>
                                <textarea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} placeholder="Ex: Pagamento via PIX, referente a vendas de Janeiro/2026" className="vt-notes-textarea" rows={3} />
                            </div>
                            <div className="vt-payment-info-box">
                                <Clock size={16} />
                                <p>Ao registrar o pagamento, um email será enviado automaticamente para o vendedor confirmando o recebimento.</p>
                            </div>
                        </div>
                        <div className="vt-modal-footer">
                            <button className="vt-btn-cancel" onClick={() => setShowPaymentModal(false)} disabled={uploadingPayment}>Cancelar</button>
                            <button className="vt-btn-confirm" onClick={handleRegisterPayment} disabled={uploadingPayment || !proofFile}>
                                {uploadingPayment ? <><div className="vt-spinner-small"></div>Enviando...</> : 'Registrar Pagamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: AJUSTAR TAXA DE COMISSÃO */}
            {showCommissionModal && selectedVendor && (
                <div className="vt-modal-overlay" onClick={() => setShowCommissionModal(false)}>
                    <div className="vt-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="vt-modal-header">
                            <h3>Ajustar Taxa de Comissão</h3>
                            <button className="vt-btn-close-modal" onClick={() => setShowCommissionModal(false)}><X size={20} /></button>
                        </div>
                        <div className="vt-modal-body">
                            <p className="vt-modal-vendor-name"><strong>{selectedVendor.username}</strong></p>
                            <p className="vt-modal-description">Define o limite máximo de desconto que o vendedor pode oferecer. O desconto sai da comissão dele.</p>
                            <div className="vt-commission-input-group">
                                <label>Taxa de Comissão (%)</label>
                                <div className="vt-input-with-symbol">
                                    <input type="number" min="0" max="100" value={newCommissionRate} onChange={(e) => setNewCommissionRate(Math.min(100, Math.max(0, Number(e.target.value))))} className="vt-commission-input" />
                                    <span className="vt-percent-symbol">%</span>
                                </div>
                                <input type="range" min="0" max="100" value={newCommissionRate} onChange={(e) => setNewCommissionRate(Number(e.target.value))} className="vt-commission-slider" />
                                <div className="vt-slider-labels"><span>0%</span><span>50%</span><span>100%</span></div>
                            </div>
                        </div>
                        <div className="vt-modal-footer">
                            <button className="vt-btn-cancel" onClick={() => setShowCommissionModal(false)}>Cancelar</button>
                            <button className="vt-btn-confirm" onClick={handleAdjustCommission}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: CONFIRMAR ATIVAR/DESATIVAR */}
            {showToggleModal && selectedVendor && (
                <div className="vt-modal-overlay" onClick={() => setShowToggleModal(false)}>
                    <div className="vt-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="vt-modal-header">
                            <h3>Confirmar Ação</h3>
                            <button className="vt-btn-close-modal" onClick={() => setShowToggleModal(false)}><X size={20} /></button>
                        </div>
                        <div className="vt-modal-body">
                            <div className="vt-confirm-icon"><AlertTriangle size={48} /></div>
                            <p className="vt-confirm-text">
                                Tem certeza que deseja <strong>{selectedVendor.enabled ? 'DESATIVAR' : 'ATIVAR'}</strong> este vendedor?
                            </p>
                            <p className="vt-modal-vendor-name"><strong>{selectedVendor.username}</strong></p>
                            {selectedVendor.enabled && (
                                <div className="vt-warning-box">
                                    <AlertTriangle size={14} />
                                    <span>Todos os cupons dele serão bloqueados.</span>
                                </div>
                            )}
                        </div>
                        <div className="vt-modal-footer">
                            <button className="vt-btn-cancel" onClick={() => setShowToggleModal(false)}>Cancelar</button>
                            <button className={selectedVendor.enabled ? 'vt-btn-danger' : 'vt-btn-confirm'} onClick={handleToggleVendor}>
                                {selectedVendor.enabled ? 'Desativar' : 'Ativar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: EXCLUIR PAGAMENTO */}
            {showDeletePaymentModal && paymentToDelete && (
                <div className="vt-modal-overlay" onClick={() => setShowDeletePaymentModal(false)}>
                    <div className="vt-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="vt-modal-header">
                            <h3>Excluir Pagamento</h3>
                            <button className="vt-btn-close-modal" onClick={() => setShowDeletePaymentModal(false)}><X size={20} /></button>
                        </div>
                        <div className="vt-modal-body">
                            <div className="vt-confirm-icon danger"><Trash2 size={48} /></div>
                            <p className="vt-confirm-text">Tem certeza que deseja <strong>EXCLUIR</strong> este registro de pagamento?</p>
                            <div className="vt-delete-details">
                                <div className="vt-detail-row">
                                    <span>Valor:</span>
                                    <strong>{formatCurrency(paymentToDelete.amount)}</strong>
                                </div>
                            </div>
                            <div className="vt-warning-box">
                                <AlertTriangle size={14} />
                                <span>Esta ação é <strong>irreversível</strong>. O histórico será removido permanentemente.</span>
                            </div>
                        </div>
                        <div className="vt-modal-footer">
                            <button className="vt-btn-cancel" onClick={() => setShowDeletePaymentModal(false)} disabled={deletingPayment}>Cancelar</button>
                            <button className="vt-btn-danger" onClick={handleDeletePayment} disabled={deletingPayment}>
                                {deletingPayment ? <><div className="vt-spinner-small"></div>Excluindo...</> : <><Trash2 size={16} />Excluir</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: VER METAS DO VENDEDOR */}
            {showGoalsModal && selectedVendorGoals && (
                <div className="vt-modal-overlay" onClick={() => setShowGoalsModal(false)}>
                    <div className="vt-modal-content vt-modal-goals" onClick={(e) => e.stopPropagation()}>
                        <div className="vt-modal-header">
                            <div>
                                <h3>Metas do Vendedor</h3>
                                <p className="vt-modal-vendor-name">{selectedVendorGoals.vendorName}</p>
                            </div>
                            <button className="vt-btn-close-modal" onClick={() => setShowGoalsModal(false)}><X size={20} /></button>
                        </div>
                        <div className="vt-modal-body">
                            <div className="vt-goals-overview">
                                <div className="vt-overview-stat">
                                    <span className="vt-stat-label">Total de Metas</span>
                                    <span className="vt-stat-value">{selectedVendorGoals.summary.totalGoals}</span>
                                </div>
                                <div className="vt-overview-stat">
                                    <span className="vt-stat-label">Metas Atingidas</span>
                                    <span className="vt-stat-value success">{selectedVendorGoals.summary.achievedGoals}</span>
                                </div>
                                <div className="vt-overview-stat">
                                    <span className="vt-stat-label">Score Geral</span>
                                    <span className="vt-stat-value primary">{selectedVendorGoals.summary.overallScore}%</span>
                                </div>
                            </div>
                            <div className="vt-goals-detail-list">
                                {selectedVendorGoals.goals.map((goal, idx: number) => (
                                    <div key={idx} className="vt-goal-detail-card">
                                        <div className="vt-goal-detail-header">
                                            <div className="vt-goal-type-info">
                                                <span className="vt-goal-type-icon">{getGoalTypeIcon(goal.type)}</span>
                                                <span className="vt-goal-type-name">{getGoalTypeName(goal.type)}</span>
                                            </div>
                                            <span className={`vt-goal-status-badge ${goal.achieved ? 'achieved' : 'not-achieved'}`}>
                                                {goal.achieved ? '✓ Atingida' : '○ Não atingida'}
                                            </span>
                                        </div>
                                        <div className="vt-goal-detail-body">
                                            <div className="vt-goal-values">
                                                <div className="vt-goal-value-item">
                                                    <span className="vt-value-label">Meta</span>
                                                    <span className="vt-value-amount">{goal.type === 'sales' ? `${goal.targetValue} vendas` : formatCurrency(goal.targetValue)}</span>
                                                </div>
                                                <div className="vt-goal-value-item">
                                                    <span className="vt-value-label">Atingido</span>
                                                    <span className="vt-value-amount">{goal.type === 'sales' ? `${goal.achievedValue} vendas` : formatCurrency(goal.achievedValue)}</span>
                                                </div>
                                                <div className="vt-goal-value-item">
                                                    <span className="vt-value-label">Progresso</span>
                                                    <span className={`vt-value-amount ${goal.achieved ? 'success' : ''}`}>{goal.percentage}%</span>
                                                </div>
                                            </div>
                                            <div className="vt-goal-progress-bar-container">
                                                <div className="vt-goal-progress-bar">
                                                    <div className={`vt-goal-progress-fill ${goal.achieved ? 'achieved' : ''}`} style={{ width: `${Math.min(goal.percentage, 100)}%` }}></div>
                                                </div>
                                                <span className="vt-goal-progress-text">{goal.percentage}%</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="vt-modal-footer">
                            <button className="vt-btn-cancel" onClick={() => setShowGoalsModal(false)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorsTab;
